import { useState } from 'react';
import { passwordLengthError } from '../utils/passwordPolicy';
import { PasswordStrength } from '../components/ui/PasswordStrength';
import { apiFetch } from '../utils/api';
import { FormField } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

export function ManagerSetupPage({ token, email }) {
  usePageTitle('Account Setup');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg('');

    if (passwordLengthError(password)) {
      setMsg(passwordLengthError(password));
      setMsgType('error');
      return;
    }

    if (password !== confirmPassword) {
      setMsg('Passwords do not match.');
      setMsgType('error');
      return;
    }

    setLoading(true);
    const data = await apiFetch('/api/managers/setup', {
      method: 'POST',
      body: JSON.stringify({ token, email, password, confirmPassword }),
    });

    if (data.ok) {
      setDone(true);
      setMsg(data.message);
      setMsgType('success');
    } else {
      setMsg(data.message);
      setMsgType('error');
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '64px', height: '64px',
            background: 'linear-gradient(135deg, #f59e0b, #9a3412)',
            borderRadius: '20px',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px', margin: '0 auto 16px',
            boxShadow: '0 8px 24px rgba(245,158,11,0.3)',
          }}>
            🍜
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '26px', fontWeight: '800',
            color: 'var(--text)', marginBottom: '6px',
          }}>
            Set Up Your Account
          </h1>
          <p style={{ color: 'var(--text-3)', fontSize: '14px' }}>
            Welcome to Scheduler! Create your password to get started.
          </p>
        </div>

        <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Read our <a href="/legal" target="_blank" rel="noreferrer">Terms of Use and Privacy &amp; Attendance Notice</a>. After signing in, you will review them before continuing.</p>
        <div className="card">
          {done ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
              <div style={{
                fontFamily: 'var(--font-display)',
                fontSize: '18px', fontWeight: '800',
                color: 'var(--text)', marginBottom: '8px',
              }}>
                Account Created!
              </div>
              <p style={{ color: 'var(--text-3)', fontSize: '14px', marginBottom: '20px' }}>
                Your account has been set up. You can now log in.
              </p>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #9a3412)',
                  color: '#fff', border: 'none',
                  borderRadius: '10px', padding: '13px 24px',
                  fontSize: '14px', fontWeight: '700',
                  cursor: 'pointer', fontFamily: 'inherit',
                  width: '100%',
                }}
              >
                Go to Login →
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <FormField label="Email">
                <input
                  className="input"
                  type="email"
                  value={email}
                  readOnly
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-3)' }}
                />
              </FormField>
              <FormField label="Create Password" hint="at least 15 characters" required>
                <input
                  className="input"
                  type="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </FormField>
              <PasswordStrength value={password} email={email} />
              <FormField label="Confirm Password" required>
                <input
                  className="input"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </FormField>

              {msg && (
                <div style={{
                  background: msgType === 'success' ? 'var(--success-bg)' : 'var(--danger-bg)',
                  border: `1px solid ${msgType === 'success' ? 'var(--success-border)' : 'var(--danger-border)'}`,
                  color: msgType === 'success' ? 'var(--success)' : 'var(--danger)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '16px',
                }}>
                  {msg}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #f59e0b, #9a3412)',
                  color: '#fff', border: 'none',
                  borderRadius: '10px', padding: '13px 24px',
                  fontSize: '14px', fontWeight: '700',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', width: '100%',
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account →'}
              </button>
            </form>
          )}
        </div>

        <p style={{
          textAlign: 'center', marginTop: '16px',
          fontSize: '12px', color: 'var(--text-4)',
        }}>
          Already have an account?{' '}
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: 'none', border: 'none',
              color: 'var(--brand-dark)', cursor: 'pointer',
              fontWeight: '700', fontSize: '12px',
              fontFamily: 'inherit',
            }}
          >
            Log in here
          </button>
        </p>
      </div>
    </div>
  );
}
